import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { ArrowLeft, Plus, Store, Trash2, Users } from 'lucide-react';
import { z } from 'zod';
import { Button, Card, CardContent, Input } from '@caratuva/ui';
import {
  CreateInvoiceInput,
  Incoterm,
  MIN_PAYABLE_USD,
  isBelowMinPayableUsd,
} from '@caratuva/shared-types';
import type {
  ConnectedAccountDTO,
  CreateInvoiceInput as CreateInvoiceInputType,
} from '@caratuva/shared-types';
import {
  ApiError,
  createInvoice,
  getInvoice,
  listConnectedAccounts,
  listInvoices,
} from '../lib/api';
import {
  BuyerPicker,
  Field,
  FieldError,
  Section,
  SUPPORTED_CURRENCIES,
  selectClass,
  type KnownBuyer,
} from '../components/invoice-form';

/*
 * Invoice-create form.
 *
 * The DTO contract is owned by `CreateInvoiceInput` in @caratuva/shared-types.
 * We keep raw text in local state (so users can type freely) and zod-validate
 * once at submit time — that way decimal regex validation reads cleanly and
 * any 400 from the backend can be surfaced inline.
 *
 * Idempotency-Key is generated with `crypto.randomUUID()` at form mount and
 * stays stable across retries until the user explicitly creates a NEW form.
 * That means a double-click on submit (or a network blip) won't create two
 * invoices — the API returns the same row.
 */

// Optional `?from=<invoiceId>` selects a source invoice to clone. The form
// fetches it and prefills buyer + line items + export references; the user
// reviews and submits to create a new draft. We deliberately do NOT create
// a draft on click — see ADR notes in the action panel.
const InvoiceCreateSearch = z.object({
  from: z.string().min(1).optional(),
});

export const Route = createFileRoute('/_app/invoices/new')({
  validateSearch: InvoiceCreateSearch,
  // No org-level gate here anymore: every invoice now routes to a connected
  // SELLER, so eligibility is per-seller (KYB approved + PIX configured =
  // readyToCollect). The form fetches ready sellers on mount and renders a CTA
  // to onboard one when none exist; the server enforces the same readiness on
  // POST /v1/invoices (assertSellerCanCollect).
  component: InvoiceCreate,
});

const INCOTERMS = Incoterm.options;

interface LineItemDraft {
  description: string;
  quantity: string;
  unitPrice: string;
}

function emptyLineItem(): LineItemDraft {
  return { description: '', quantity: '1', unitPrice: '0.00' };
}

function formatDecimal2(value: number): string {
  if (!Number.isFinite(value)) return '0.00';
  return value.toFixed(2);
}

function computeSubtotal(quantity: string, unitPrice: string): string {
  const q = Number.parseFloat(quantity);
  const p = Number.parseFloat(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return '0.00';
  return formatDecimal2(q * p);
}

function newIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Vanishingly rare fallback for very old browsers; the dashboard targets
  // evergreen browsers so this is just defensive.
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

type FieldErrors = Partial<Record<string, string>>;

function InvoiceCreate(): JSX.Element {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { from: sourceInvoiceId } = Route.useSearch();

  const [idempotencyKey] = useState<string>(() => newIdempotencyKey());

  // Every invoice settles to a connected SELLER. Fetch approved sellers and
  // keep only the ones the server will actually let us invoice (readyToCollect:
  // KYB approved + offramp wallet + PIX). Selection is required.
  const sellersQuery = useQuery({
    queryKey: ['connected-accounts', 'seller', 'approved'],
    queryFn: ({ signal }) => listConnectedAccounts({ role: 'seller', status: 'approved' }, signal),
    staleTime: 30_000,
  });
  const readySellers = useMemo<ConnectedAccountDTO[]>(
    () => (sellersQuery.data?.data ?? []).filter((s) => s.readyToCollect && s.externalId != null),
    [sellersQuery.data],
  );
  const [sellerExternalId, setSellerExternalId] = useState('');
  // Auto-select when exactly one seller is ready (and the user hasn't picked).
  useEffect(() => {
    if (sellerExternalId) return;
    if (readySellers.length === 1) {
      setSellerExternalId(readySellers[0]!.externalId as string);
    }
  }, [readySellers, sellerExternalId]);

  // Optionally invoice a PRE-CREATED, KYC-approved buyer account (role=buyer):
  // referencing it by externalId lets the buyer skip pay-time KYC. Optional —
  // when none is picked the invoice falls back to a walk-up buyer (email/name).
  const buyersQuery = useQuery({
    queryKey: ['connected-accounts', 'buyer', 'approved'],
    queryFn: ({ signal }) => listConnectedAccounts({ role: 'buyer', status: 'approved' }, signal),
    staleTime: 30_000,
  });
  const approvedBuyers = useMemo<ConnectedAccountDTO[]>(
    () => (buyersQuery.data?.data ?? []).filter((b) => b.externalId != null),
    [buyersQuery.data],
  );
  const [buyerExternalId, setBuyerExternalId] = useState('');

  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [currency, setCurrency] = useState<string>('USD');
  const [dueDate, setDueDate] = useState('');
  const [buyerRef, setBuyerRef] = useState('');
  const [dueNumber, setDueNumber] = useState('');
  const [exportContractRef, setExportContractRef] = useState('');
  const [incoterm, setIncoterm] = useState<string>('');
  const [portOfLoading, setPortOfLoading] = useState('');
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([emptyLineItem()]);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [conflictInvoiceId, setConflictInvoiceId] = useState<string | null>(null);

  // Duplicate-source prefill. Skips fields that must be fresh per shipment:
  // invoice number / publicId, status, payment events, dueDate (defaulted),
  // and the DU-E number — that one is a Brazilian export-declaration ID
  // tied to the original shipment and reusing it would corrupt records.
  const sourceQuery = useQuery({
    queryKey: ['invoice', sourceInvoiceId],
    queryFn: ({ signal }) => getInvoice(sourceInvoiceId as string, signal),
    enabled: Boolean(sourceInvoiceId),
    staleTime: 60_000,
  });
  const [prefillApplied, setPrefillApplied] = useState(false);
  useEffect(() => {
    if (!sourceInvoiceId || prefillApplied) return;
    const src = sourceQuery.data;
    if (!src) return;
    setBuyerEmail(src.buyerEmail ?? '');
    setBuyerName(src.buyerName ?? '');
    // Carry the source seller forward only if it is still ready to collect.
    if (src.sellerExternalId && readySellers.some((s) => s.externalId === src.sellerExternalId)) {
      setSellerExternalId(src.sellerExternalId);
    }
    setCurrency(src.currency);
    setBuyerRef(src.buyerRef ?? '');
    setExportContractRef(src.exportContractRef ?? '');
    setIncoterm(src.incoterm ?? '');
    setPortOfLoading(src.portOfLoading ?? '');
    if (src.lineItems && src.lineItems.length > 0) {
      setLineItems(
        src.lineItems.map((li) => ({
          description: li.description,
          quantity: li.quantity,
          unitPrice: li.unitPrice,
        })),
      );
    }
    setPrefillApplied(true);
  }, [sourceInvoiceId, sourceQuery.data, prefillApplied, readySellers]);

  // Past buyers come from the org's invoice history — we piggy-back on the
  // existing /v1/invoices listing rather than add a /v1/buyers endpoint,
  // since buyers are already persisted by the create flow (see
  // InvoicesService.create -> buyer.upsert).
  const knownBuyersQuery = useQuery({
    queryKey: ['invoices'],
    queryFn: ({ signal }) => listInvoices(signal),
    staleTime: 60_000,
  });
  const knownBuyers = useMemo<KnownBuyer[]>(() => {
    const byEmail = new Map<string, KnownBuyer>();
    for (const inv of knownBuyersQuery.data ?? []) {
      const email = inv.buyerEmail;
      if (!email) continue;
      const existing = byEmail.get(email);
      const lastUsedAt = inv.createdAt;
      if (!existing || lastUsedAt > existing.lastUsedAt) {
        byEmail.set(email, { email, name: inv.buyerName ?? null, lastUsedAt });
      }
    }
    return Array.from(byEmail.values()).sort((a, b) =>
      a.lastUsedAt < b.lastUsedAt ? 1 : a.lastUsedAt > b.lastUsedAt ? -1 : 0,
    );
  }, [knownBuyersQuery.data]);

  const [pickerOpen, setPickerOpen] = useState(false);
  function selectBuyer(b: KnownBuyer): void {
    setBuyerEmail(b.email);
    setBuyerName(b.name ?? '');
    setPickerOpen(false);
  }

  const subtotals = useMemo(
    () => lineItems.map((li) => computeSubtotal(li.quantity, li.unitPrice)),
    [lineItems],
  );

  const total = useMemo(() => {
    const sum = subtotals.reduce((acc, s) => acc + Number.parseFloat(s || '0'), 0);
    return formatDecimal2(sum);
  }, [subtotals]);

  // Only assert the threshold for USD, where the invoice total equals the
  // amount the buyer funds. For EUR/BRL the buyer's sent amount is converted,
  // so we surface the informational note (below) without a hard check.
  const minPayableLabel = intl.formatNumber(MIN_PAYABLE_USD, {
    style: 'currency',
    currency: 'USD',
  });
  // Reuse the shared check (USD-only) the API enforces; gate the warning on a
  // positive total so a fresh, empty form doesn't nag at $0.00.
  const totalNumber = Number.parseFloat(total);
  const belowMinimum = totalNumber > 0 && isBelowMinPayableUsd(total, currency);

  const mutation = useMutation({
    mutationFn: (input: CreateInvoiceInputType) => createInvoice(input, { idempotencyKey }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void navigate({ to: '/invoices/$id', params: { id: data.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        // 400 with field-level errors — the API mirrors zod issues in `message`.
        if (err.status === 400) {
          // Seller-onboarding gate (assertSellerCanCollect). The form only lists
          // customers that are ready to collect and otherwise links to onboard
          // one at /customers, so this only fires if eligibility lapsed after
          // the form loaded.
          const gate = (err.body as { error?: string } | null)?.error;
          if (
            gate === 'PixPayoutKeyMissing' ||
            gate === 'OfframpWalletMissing' ||
            gate === 'KybNotApproved'
          ) {
            setFormError(
              intl.formatMessage({
                id: 'invoices.create.error.payoutNotReady',
                defaultMessage:
                  'Complete business verification (KYB) and register a PIX payout key in Payout settings before creating invoices.',
              }),
            );
            return;
          }
          const next: FieldErrors = {};
          const body = err.body as { message?: unknown } | null;
          const messages = Array.isArray(body?.message)
            ? (body.message as string[])
            : typeof body?.message === 'string'
              ? [body.message]
              : [];
          for (const m of messages) {
            // zod messages typically look like "lineItems.0.unitPrice: decimal(20,2)".
            const match = /^([a-zA-Z0-9_.[\]]+)[:\s-]+\s*(.+)$/.exec(m);
            if (match && match[1] && match[2]) {
              next[match[1]] = match[2];
            }
          }
          setFieldErrors(next);
          setFormError(
            messages.length > 0
              ? messages.join('; ')
              : intl.formatMessage({
                  id: 'invoices.create.error.validation',
                  defaultMessage: 'Please fix the highlighted fields.',
                }),
          );
          return;
        }
        if (err.status === 409) {
          // Idempotency-Key collision — the API returns the existing invoice.
          // Older error responses may include the colliding id in `body`.
          const body = err.body as { invoiceId?: string; id?: string } | null;
          const id = body?.invoiceId ?? body?.id ?? null;
          setConflictInvoiceId(id);
          setFormError(
            intl.formatMessage({
              id: 'invoices.create.error.conflict',
              defaultMessage: 'An invoice with this reference already exists.',
            }),
          );
          return;
        }
      }
      setFormError(
        err instanceof Error
          ? err.message
          : intl.formatMessage({
              id: 'invoices.create.error.generic',
              defaultMessage: 'Failed to create invoice.',
            }),
      );
    },
  });

  function addLineItem(): void {
    setLineItems((items) => [...items, emptyLineItem()]);
  }

  function removeLineItem(index: number): void {
    setLineItems((items) => (items.length <= 1 ? items : items.filter((_, i) => i !== index)));
  }

  function updateLineItem(index: number, patch: Partial<LineItemDraft>): void {
    setLineItems((items) => items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    setConflictInvoiceId(null);

    const candidate: Record<string, unknown> = {
      currency: currency.trim().toUpperCase(),
      amount: total,
      sellerExternalId: sellerExternalId.trim(),
      lineItems: lineItems.map((li, i) => ({
        description: li.description.trim(),
        quantity: li.quantity.trim(),
        unitPrice: li.unitPrice.trim(),
        subtotal: subtotals[i] ?? '0.00',
      })),
    };
    // Buyer: a pre-created, KYC-approved buyer account (skips pay-time KYC) OR a
    // walk-up buyer keyed by email/name. The API contract requires exactly one.
    if (buyerExternalId) {
      candidate.buyerExternalId = buyerExternalId;
    } else {
      candidate.buyerEmail = buyerEmail.trim();
      candidate.buyerName = buyerName.trim();
    }
    if (dueDate) candidate.dueDate = new Date(dueDate).toISOString();
    if (buyerRef.trim()) candidate.buyerRef = buyerRef.trim();
    if (dueNumber.trim()) candidate.dueNumber = dueNumber.trim();
    if (exportContractRef.trim()) candidate.exportContractRef = exportContractRef.trim();
    if (incoterm) candidate.incoterm = incoterm;
    if (portOfLoading.trim()) candidate.portOfLoading = portOfLoading.trim();

    const parsed = CreateInvoiceInput.safeParse(candidate);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        if (!next[path]) {
          next[path] = issue.message;
        }
      }
      setFieldErrors(next);
      setFormError(
        intl.formatMessage({
          id: 'invoices.create.error.validation',
          defaultMessage: 'Please fix the highlighted fields.',
        }),
      );
      return;
    }

    mutation.mutate(parsed.data);
  }

  function lineItemError(
    index: number,
    field: keyof LineItemDraft | 'subtotal',
  ): string | undefined {
    return fieldErrors[`lineItems.${index}.${field}`];
  }

  return (
    <section className="mx-auto max-w-5xl">
      <Link
        to="/invoices"
        className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        {intl.formatMessage({ id: 'nav.invoices', defaultMessage: 'Invoices' })}
      </Link>

      <div className="mt-3 mb-10 border-b border-navy-100 pb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-navy-900">
          {sourceInvoiceId
            ? intl.formatMessage({
                id: 'invoices.create.duplicate.title',
                defaultMessage: 'Duplicate invoice',
              })
            : intl.formatMessage({ id: 'invoices.create.title', defaultMessage: 'New invoice' })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {sourceInvoiceId
            ? intl.formatMessage({
                id: 'invoices.create.duplicate.subtitle',
                defaultMessage:
                  'Review the prefilled fields and submit to create a new draft. Invoice number, due date, and DU-E always reset.',
              })
            : intl.formatMessage({
                id: 'invoices.create.subtitle',
                defaultMessage: 'Send an invoice to your buyer and start tracking payment.',
              })}
        </p>
        {sourceInvoiceId && sourceQuery.isPending ? (
          <p className="mt-3 text-sm text-navy-600">
            {intl.formatMessage({
              id: 'invoices.create.duplicate.loading',
              defaultMessage: 'Loading source invoice...',
            })}
          </p>
        ) : null}
        {sourceInvoiceId && sourceQuery.isError ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-[var(--color-danger)] bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]"
          >
            {intl.formatMessage({
              id: 'invoices.create.duplicate.error',
              defaultMessage:
                'Could not load the source invoice. The form is empty — fill it in or go back.',
            })}
          </p>
        ) : null}
        {sourceInvoiceId && sourceQuery.data ? (
          <div className="mt-3 rounded-md border border-navy-100 bg-beige-50/60 p-3 text-sm text-navy-700">
            {intl.formatMessage(
              {
                id: 'invoices.create.duplicate.banner',
                defaultMessage: 'Prefilled from {publicId}.',
              },
              { publicId: sourceQuery.data.publicId },
            )}
          </div>
        ) : null}
      </div>

      {sellersQuery.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'invoices.create.customers.loading',
            defaultMessage: 'Loading customers...',
          })}
        </p>
      ) : sellersQuery.isError ? (
        <p
          role="alert"
          className="rounded-md border border-[var(--color-danger)] bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]"
        >
          {intl.formatMessage({
            id: 'invoices.create.customers.error',
            defaultMessage: 'Failed to load customers. Refresh to try again.',
          })}
        </p>
      ) : readySellers.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-navy-200 bg-white px-6 py-12 text-center">
          <Store aria-hidden className="h-7 w-7 text-navy-500" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-navy-900">
              {intl.formatMessage({
                id: 'invoices.create.customers.empty.title',
                defaultMessage: 'No customer is ready to collect yet',
              })}
            </p>
            <p className="mt-1 text-sm text-navy-600">
              {intl.formatMessage({
                id: 'invoices.create.customers.empty.description',
                defaultMessage:
                  'Invoices settle to a verified customer. Onboard a customer (verification + bank account) before creating an invoice.',
              })}
            </p>
          </div>
          <Button asChild>
            <Link to="/customers/new">
              <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
              {intl.formatMessage({ id: 'customers.new', defaultMessage: 'New customer' })}
            </Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-10" noValidate>
          <Section
            title={intl.formatMessage({
              id: 'invoices.create.section.customer',
              defaultMessage: 'Customer',
            })}
            description={intl.formatMessage({
              id: 'invoices.create.section.customer.description',
              defaultMessage: 'The verified customer this invoice collects for and settles to.',
            })}
          >
            <Card className="border-navy-100 shadow-xs">
              <CardContent className="p-6">
                <Field
                  id="sellerExternalId"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.customer',
                    defaultMessage: 'Customer',
                  })}
                  required
                  error={fieldErrors.sellerExternalId}
                >
                  <select
                    id="sellerExternalId"
                    value={sellerExternalId}
                    onChange={(e) => setSellerExternalId(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="" disabled>
                      {intl.formatMessage({
                        id: 'invoices.create.field.customer.placeholder',
                        defaultMessage: 'Select a customer',
                      })}
                    </option>
                    {readySellers.map((s) => (
                      <option key={s.id} value={s.externalId as string}>
                        {(s.displayName ?? s.legalName ?? s.externalId) as string}
                      </option>
                    ))}
                  </select>
                </Field>
              </CardContent>
            </Card>
          </Section>
          <Section
            title={intl.formatMessage({
              id: 'invoices.create.section.buyer',
              defaultMessage: 'Buyer',
            })}
            description={intl.formatMessage({
              id: 'invoices.create.section.buyer.description',
              defaultMessage: 'Where the invoice is sent and who pays it.',
            })}
          >
            <Card className="border-navy-100 shadow-xs">
              <CardContent className="space-y-5 p-6">
                {approvedBuyers.length > 0 ? (
                  <Field
                    id="buyerExternalId"
                    label={intl.formatMessage({
                      id: 'invoices.create.field.buyerAccount',
                      defaultMessage: 'Buyer account (optional)',
                    })}
                  >
                    <select
                      id="buyerExternalId"
                      value={buyerExternalId}
                      onChange={(e) => setBuyerExternalId(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: 'invoices.create.buyer.walkUp',
                          defaultMessage: 'New buyer — KYC at pay-time',
                        })}
                      </option>
                      {approvedBuyers.map((b) => (
                        <option key={b.id} value={b.externalId as string}>
                          {(b.displayName ?? b.legalName ?? b.email ?? b.externalId) as string}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                {buyerExternalId ? (
                  <p className="text-sm text-navy-500">
                    {intl.formatMessage({
                      id: 'invoices.create.buyer.preApproved',
                      defaultMessage:
                        'This buyer is already KYC-approved and will skip verification at pay-time.',
                    })}
                  </p>
                ) : (
                  <>
                    {knownBuyers.length > 0 ? (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPickerOpen(true)}
                        >
                          <Users aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          {intl.formatMessage({
                            id: 'invoices.create.buyer.pickExisting',
                            defaultMessage: 'Choose existing buyer',
                          })}
                        </Button>
                      </div>
                    ) : null}
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        id="buyerEmail"
                        label={intl.formatMessage({
                          id: 'invoices.create.field.buyerEmail',
                          defaultMessage: 'Buyer email',
                        })}
                        required
                        error={fieldErrors.buyerEmail}
                      >
                        <Input
                          id="buyerEmail"
                          type="email"
                          autoComplete="email"
                          required
                          value={buyerEmail}
                          onChange={(e) => setBuyerEmail(e.target.value)}
                        />
                      </Field>
                      <Field
                        id="buyerName"
                        label={intl.formatMessage({
                          id: 'invoices.create.field.buyerName',
                          defaultMessage: 'Buyer name',
                        })}
                        required
                        error={fieldErrors.buyerName}
                      >
                        <Input
                          id="buyerName"
                          required
                          value={buyerName}
                          onChange={(e) => setBuyerName(e.target.value)}
                        />
                      </Field>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            <BuyerPicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              buyers={knownBuyers}
              onSelect={selectBuyer}
            />
          </Section>

          <Section
            title={intl.formatMessage({
              id: 'invoices.create.section.lineItems',
              defaultMessage: 'Line items',
            })}
            description={intl.formatMessage({
              id: 'invoices.create.section.lineItems.description',
              defaultMessage: 'Subtotal is computed as quantity multiplied by unit price.',
            })}
          >
            <Card className="overflow-hidden border-navy-100 shadow-xs">
              <CardContent className="p-0">
                <div className="hidden grid-cols-12 gap-3 border-b border-navy-100 px-6 py-3 text-[11px] font-medium uppercase tracking-wider text-navy-700 sm:grid">
                  <div className="col-span-5">
                    {intl.formatMessage({
                      id: 'invoices.create.field.description',
                      defaultMessage: 'Description',
                    })}
                  </div>
                  <div className="col-span-2">
                    {intl.formatMessage({
                      id: 'invoices.create.field.quantity',
                      defaultMessage: 'Quantity',
                    })}
                  </div>
                  <div className="col-span-2">
                    {intl.formatMessage({
                      id: 'invoices.create.field.unitPrice',
                      defaultMessage: 'Unit price',
                    })}
                  </div>
                  <div className="col-span-2 text-right">
                    {intl.formatMessage({
                      id: 'invoices.create.field.subtotal',
                      defaultMessage: 'Subtotal',
                    })}
                  </div>
                  <div className="col-span-1" />
                </div>

                <div className="divide-y divide-navy-100">
                  {lineItems.map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 items-start gap-3 px-6 py-4 transition-colors hover:bg-beige-50/40 sm:grid-cols-12"
                    >
                      <div className="sm:col-span-5">
                        <Input
                          aria-label={intl.formatMessage(
                            {
                              id: 'invoices.create.field.descriptionAria',
                              defaultMessage: 'Description for line {index}',
                            },
                            { index: index + 1 },
                          )}
                          placeholder={intl.formatMessage({
                            id: 'invoices.create.field.description.placeholder',
                            defaultMessage: 'Item or service',
                          })}
                          value={item.description}
                          onChange={(e) => updateLineItem(index, { description: e.target.value })}
                        />
                        {lineItemError(index, 'description') ? (
                          <FieldError>{lineItemError(index, 'description')}</FieldError>
                        ) : null}
                      </div>
                      <div className="sm:col-span-2">
                        <Input
                          aria-label={intl.formatMessage(
                            {
                              id: 'invoices.create.field.quantityAria',
                              defaultMessage: 'Quantity for line {index}',
                            },
                            { index: index + 1 },
                          )}
                          inputMode="decimal"
                          className="text-right tabular-nums"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, { quantity: e.target.value })}
                        />
                        {lineItemError(index, 'quantity') ? (
                          <FieldError>{lineItemError(index, 'quantity')}</FieldError>
                        ) : null}
                      </div>
                      <div className="sm:col-span-2">
                        <Input
                          aria-label={intl.formatMessage(
                            {
                              id: 'invoices.create.field.unitPriceAria',
                              defaultMessage: 'Unit price for line {index}',
                            },
                            { index: index + 1 },
                          )}
                          inputMode="decimal"
                          className="text-right tabular-nums"
                          value={item.unitPrice}
                          onChange={(e) => updateLineItem(index, { unitPrice: e.target.value })}
                        />
                        {lineItemError(index, 'unitPrice') ? (
                          <FieldError>{lineItemError(index, 'unitPrice')}</FieldError>
                        ) : null}
                      </div>
                      <div className="self-center text-right text-sm font-medium tabular-nums text-navy-900 sm:col-span-2">
                        {subtotals[index] ?? '0.00'}
                      </div>
                      <div className="flex justify-end self-center sm:col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLineItem(index)}
                          disabled={lineItems.length <= 1}
                          aria-label={intl.formatMessage(
                            {
                              id: 'invoices.create.removeLineItemAria',
                              defaultMessage: 'Remove line {index}',
                            },
                            { index: index + 1 },
                          )}
                          className="text-navy-600 hover:bg-danger/10 hover:text-danger"
                        >
                          <Trash2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {fieldErrors.lineItems ? (
                  <div className="px-6 pt-3">
                    <FieldError>{fieldErrors.lineItems}</FieldError>
                  </div>
                ) : null}

                <div className="flex items-center justify-between border-t border-navy-100 bg-beige-50/60 px-6 py-4">
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                    {intl.formatMessage({
                      id: 'invoices.create.addLineItem',
                      defaultMessage: 'Add line item',
                    })}
                  </Button>
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs font-medium uppercase tracking-wider text-navy-700">
                      {intl.formatMessage({
                        id: 'invoices.create.total',
                        defaultMessage: 'Total',
                      })}
                    </span>
                    <span className="font-mono text-lg font-semibold tabular-nums text-navy-900">
                      {total}
                    </span>
                    <span className="text-sm font-medium text-navy-700">{currency}</span>
                  </div>
                </div>

                <div className="border-t border-navy-100 px-6 py-3">
                  {belowMinimum ? (
                    <p className="text-xs text-[var(--color-danger)]" role="alert">
                      {intl.formatMessage(
                        {
                          id: 'invoices.create.minAmount.below',
                          defaultMessage:
                            'Below the {min} minimum your buyer can pay by bank transfer — increase the total or this invoice can’t be funded online.',
                        },
                        { min: minPayableLabel },
                      )}
                    </p>
                  ) : (
                    <p className="text-xs text-navy-600">
                      {intl.formatMessage(
                        {
                          id: 'invoices.create.minAmount.note',
                          defaultMessage:
                            'Buyers pay by bank transfer through our payments partner, which requires a minimum of about {min} (or equivalent). Smaller invoices can’t be funded online.',
                        },
                        { min: minPayableLabel },
                      )}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </Section>

          <Section
            title={intl.formatMessage({
              id: 'invoices.create.section.details',
              defaultMessage: 'Invoice details',
            })}
            description={intl.formatMessage({
              id: 'invoices.create.section.details.description',
              defaultMessage: 'Currency, due date, and optional export trade references.',
            })}
          >
            <Card className="border-navy-100 shadow-xs">
              <CardContent className="grid gap-5 p-6 sm:grid-cols-2">
                <Field
                  id="currency"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.currency',
                    defaultMessage: 'Currency',
                  })}
                  error={fieldErrors.currency}
                >
                  <select
                    id="currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className={selectClass}
                  >
                    {SUPPORTED_CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="dueDate"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.dueDate',
                    defaultMessage: 'Due date',
                  })}
                  error={fieldErrors.dueDate}
                >
                  <Input
                    id="dueDate"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </Field>
                <Field
                  id="buyerRef"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.buyerRef',
                    defaultMessage: 'Buyer reference',
                  })}
                  error={fieldErrors.buyerRef}
                >
                  <Input
                    id="buyerRef"
                    value={buyerRef}
                    onChange={(e) => setBuyerRef(e.target.value)}
                  />
                </Field>
                <Field
                  id="dueNumber"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.dueNumber',
                    defaultMessage: 'DU-E number',
                  })}
                  error={fieldErrors.dueNumber}
                >
                  <Input
                    id="dueNumber"
                    value={dueNumber}
                    onChange={(e) => setDueNumber(e.target.value)}
                    placeholder="11 digits"
                  />
                </Field>
                <Field
                  id="exportContractRef"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.exportContractRef',
                    defaultMessage: 'Export contract ref',
                  })}
                  error={fieldErrors.exportContractRef}
                >
                  <Input
                    id="exportContractRef"
                    value={exportContractRef}
                    onChange={(e) => setExportContractRef(e.target.value)}
                  />
                </Field>
                <Field
                  id="incoterm"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.incoterm',
                    defaultMessage: 'Incoterm',
                  })}
                  error={fieldErrors.incoterm}
                >
                  <select
                    id="incoterm"
                    value={incoterm}
                    onChange={(e) => setIncoterm(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">
                      {intl.formatMessage({
                        id: 'invoices.create.field.incoterm.none',
                        defaultMessage: 'None',
                      })}
                    </option>
                    {INCOTERMS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="portOfLoading"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.portOfLoading',
                    defaultMessage: 'Port of loading',
                  })}
                  error={fieldErrors.portOfLoading}
                >
                  <Input
                    id="portOfLoading"
                    value={portOfLoading}
                    onChange={(e) => setPortOfLoading(e.target.value)}
                  />
                </Field>
              </CardContent>
            </Card>
          </Section>

          {formError ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-danger)] bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]"
            >
              <p>{formError}</p>
              {conflictInvoiceId ? (
                <p className="mt-2">
                  <Link to="/invoices/$id" params={{ id: conflictInvoiceId }} className="underline">
                    {intl.formatMessage({
                      id: 'invoices.create.error.conflict.link',
                      defaultMessage: 'Open existing invoice',
                    })}
                  </Link>
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3 border-t border-navy-100 pt-6">
            <Button asChild variant="ghost">
              <Link to="/invoices">
                {intl.formatMessage({
                  id: 'invoices.create.cancel',
                  defaultMessage: 'Cancel',
                })}
              </Link>
            </Button>
            <Button type="submit" disabled={mutation.isPending || !sellerExternalId}>
              {mutation.isPending
                ? intl.formatMessage({
                    id: 'invoices.create.submitting',
                    defaultMessage: 'Creating...',
                  })
                : intl.formatMessage({
                    id: 'invoices.create.submit',
                    defaultMessage: 'Create invoice',
                  })}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
